import { EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_WATER_TRANSFER_ID } from 'constants/experience'
import { type ExperienceListFilters } from 'actions/ExperienceActions'
import type { ExperiencesCheckoutTopOfferListId } from 'checkout/selectors/experienceSelectors'

export interface ComputeParams {
  currencyCode: string
  geoCoords: { latitude?: number, longitude?: number }
  hotelOffer?: { id: string, experiencesCurated?: boolean }
  availability: { from?: string, to?: string }
  hasNoIncludedTransfer: boolean
  targetAudience?: App.ExperienceTargetAudience
  tagIds?: Array<string>
  experienceIdsNotIn?: Array<string>
}

export function computeTopOfferListFiltersFromParams(
  params: ComputeParams,
): Array<{ id: ExperiencesCheckoutTopOfferListId, filters: ExperienceListFilters }> {
  const {
    currencyCode,
    geoCoords,
    hotelOffer,
    availability,
    hasNoIncludedTransfer,
    targetAudience,
    tagIds,
    experienceIdsNotIn,
  } = params
  const { latitude, longitude } = geoCoords
  if (!latitude || !longitude) return []

  const list: Array<{ id: ExperiencesCheckoutTopOfferListId, filters: ExperienceListFilters }> = []

  // Priority 1: Offers with tags assigned list
  if (tagIds?.length) {
    list.push({
      id: 'TAG_ASSIGNED',
      filters: {
        currencyCode,
        tagIds,
        showUnlisted: true,
        experienceIdsNotIn,
      },
    })
  }

  // Priority 2: Transfers & Water Transfers
  if (hasNoIncludedTransfer) {
    list.push({
      id: 'TRANSFER',
      filters: {
        currencyCode,
        latitude,
        longitude,
        distanceRange: ['0KM', '50KM'],
        categoryCodes: [EXP_CATEGORY_AIRPORT_TRANSFER_ID],
        limit: 10,
        experienceIdsNotIn,
        ...availability,
      },
    })
  }

  list.push({
    id: 'WATER_TRANSFER',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['0KM', '50KM'],
      categoryCodes: [EXP_CATEGORY_WATER_TRANSFER_ID],
      experienceIdsNotIn,
      limit: 1,
      ...availability,
    },
  })

  // Priority 3: Hotel Offer Curated List
  if (hotelOffer?.experiencesCurated) {
    list.push({
      id: 'HOTEL_CURATED',
      filters: {
        currencyCode,
        offerId: hotelOffer.id,
        showUnlisted: true,
        limit: 2,
        experienceIdsNotIn,
        ...availability,
      },
    })
  }

  // Priority 4: Regional Offer Curated List
  list.push({
    id: 'REGIONAL_CURATED',
    filters: {
      currencyCode,
      latitude,
      longitude,
      onlyCuratedByRegion: true,
      categoryCodesNotIn: [EXP_CATEGORY_AIRPORT_LOUNGE_ID],
      experienceIdsNotIn,
      limit: 3,
      ...availability,
    },
  })

  // Priority 5: Distance based lists
  const excludeCats = [
    EXP_CATEGORY_AIRPORT_TRANSFER_ID,
    EXP_CATEGORY_AIRPORT_LOUNGE_ID,
    EXP_CATEGORY_WATER_TRANSFER_ID,
  ]

  const excludeProviders = ['cln']

  // Special lists by distance for hotel only
  if (hotelOffer) {
    list.push({
      id: 'DISTANCE_SHORT',
      filters: {
        currencyCode,
        latitude,
        longitude,
        distanceRange: ['0KM', '5KM'],
        targetAudienceIn: targetAudience ? [targetAudience] : undefined,
        categoryCodesNotIn: excludeCats,
        providerNotIn: excludeProviders,
        experienceIdsNotIn,
        limit: 2,
        ...availability,
      },
    })

    list.push({
      id: 'DISTANCE_MEDIUM',
      filters: {
        currencyCode,
        latitude,
        longitude,
        distanceRange: ['5KM', '20KM'],
        targetAudienceIn: targetAudience ? [targetAudience] : undefined,
        categoryCodesNotIn: excludeCats,
        providerNotIn: excludeProviders,
        experienceIdsNotIn,
        limit: 2,
        ...availability,
      },
    })

    list.push({
      id: 'DISTANCE_LONG',
      filters: {
        currencyCode,
        latitude,
        longitude,
        distanceRange: ['20KM', '50KM'],
        targetAudienceIn: targetAudience ? [targetAudience] : undefined,
        categoryCodesNotIn: excludeCats,
        providerNotIn: excludeProviders,
        experienceIdsNotIn,
        limit: 2,
        ...availability,
      },
    })
  }
  // Priority 6: Fallback List
  list.push({
    id: 'FALLBACK',
    filters: {
      currencyCode,
      latitude,
      longitude,
      distanceRange: ['0KM', hotelOffer ? '50KM' : '200KM'],
      targetAudienceIn: targetAudience ? [targetAudience] : undefined,
      categoryCodesNotIn: excludeCats,
      providerNotIn: excludeProviders,
      experienceIdsNotIn,
      limit: 30,
      ...availability,
    },
  })

  return list
}
export default computeTopOfferListFiltersFromParams
